import { get } from "lodash";
import { PrimeIcons } from "primereact/api";
import { classNames } from "primereact/utils";
import { useTranslation } from "react-i18next";
import { Modal, Typography } from "../../../../../common/components";
import { TYPOGRAPHY_TYPE } from "../../../../../common/components/Typography";
import { useUplinkInterval } from "../../../../devices/utils/hooks";
import { MODE_ICONS } from "../../../../energy/constants";
import { Mode } from "../../../../energy/models/mode.model";

interface ActiveModeModalProps {
    isOpen?: boolean;
    hasFanCoils?: boolean;
    hasRadiatorThermostats?: boolean;
    onClickClose?: VoidFunction;
    onSuccess?: VoidFunction;
    mode: Mode;
}

const ActiveModeModal = (props: ActiveModeModalProps) => {
    const {
        isOpen = false,
        hasFanCoils = false,
        hasRadiatorThermostats = false,
        onClickClose = () => {},
        mode,
    } = props;

    const { t } = useTranslation(["energy", "devices", "common"]);

    const { getFormattedUplinkInterval } = useUplinkInterval();

    return (
        <Modal
            className="max-w-4xl"
            isOpen={isOpen}
            onClickCancel={onClickClose}
            hasCancelButton
            title={
                <div className="flex gap-4">
                    {get(MODE_ICONS, mode.icon!, null)} {mode.name}
                </div>
            }
            onClickClose={onClickClose}
            cancelText={t("common:close")}
        >
            <div className="flex flex-col gap-6">
                <Typography type={TYPOGRAPHY_TYPE.labelLarge} className="mb-3">
                    {t("general.activeModeInfo")}
                </Typography>
                {mode.ruleOverride?.enabled && (
                    <Typography type={TYPOGRAPHY_TYPE.bodyMediumBold}>
                        {`${t("general.disableRulesSetTemperature")} ${
                            mode.ruleOverride.baseTemperature
                        }°C ${t("general.dailyResetAt")} ${
                            mode.ruleOverride.resetAt
                        }`}
                    </Typography>
                )}
                {mode.childLock && (
                    <Typography type={TYPOGRAPHY_TYPE.bodyMediumBold}>
                        {t("general.childLockActivated")}
                    </Typography>
                )}
                {mode.setUplinkInterval && (
                    <Typography type={TYPOGRAPHY_TYPE.bodyMediumBold}>
                        {`${t("general.setGlobalUplink")} ${
                            mode.uplinkIntervalInSeconds
                                ? getFormattedUplinkInterval(
                                      mode.uplinkIntervalInSeconds
                                  )
                                : t("common:default") || ""
                        }`}
                    </Typography>
                )}
                {hasFanCoils && (
                    <div className="flex gap-4">
                        <Typography type={TYPOGRAPHY_TYPE.bodyMediumBold}>
                            {t("general.operationModeForFanCoil")}
                        </Typography>
                        {t(`devices:fanCoilModes.${mode.fanCoilOperationMode}`)}
                    </div>
                )}
                {hasRadiatorThermostats && (
                    <div className="flex gap-4">
                        <Typography type={TYPOGRAPHY_TYPE.bodyMediumBold}>
                            {t("general.modeForThermostats")}
                        </Typography>
                        {t(
                            `devices:thermostatModes.${mode.radiatorOperationMode}`
                        )}
                    </div>
                )}
                <div className="flex gap-2 items-center mt-3">
                    <i
                        className={classNames(
                            PrimeIcons.EXCLAMATION_TRIANGLE,
                            "text-warning-default text-xl"
                        )}
                    />
                    {t("general.activeModeWarning")}
                </div>
            </div>
        </Modal>
    );
};

export default ActiveModeModal;
